'use client';

import { Fragment, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { Transition, TransitionChild } from '@headlessui/react';
import { AppNextLink } from '@/features/app-next-link';
import { useDisableBodyScroll } from '@/features/modal/utils/use-disable-body-scroll';
import { PUBLIC_IS_BOT } from '@/app/_constants/search-engines';
import { cn } from '@/utils/cn';
import { getCdnUrl } from '@/utils/url';
import { useCategoryModal } from './context/category-modal-context';
import { DEFAULT_SELECTED_MENU_ITEM, MOBILE_MENU_TABS, MenuTab, SPECIAL_OFFER_CHILDREN } from '../components/constants';
import Brands from '../components/mega-menu/brands';
import SearchboxHeader from '../components/mobile-header/searchbox-header';
import MenuGrouping from '../components/mobile-menu/menu-grouping';
import SubMenuContent from '../components/mobile-menu/sub-menu-content';
import { MenuItems } from '../constants';
import { MobileMenuItems, type SelectedMenuItem } from '../types';
import { useLayoutMenu } from '../utils/layout-menu/use-layout-menu';
const CategoryModal = () => {
  const {
    isOpen,
    close: closeCategoryModal
  } = useCategoryModal();
  const {
    data: layoutMenu
  } = useLayoutMenu();
  const {
    disableScroll,
    enableScroll
  } = useDisableBodyScroll(true);
  const subMenuScrollRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<MenuTab>('categories');
  const [selectedItem, setSelectedItem] = useState<SelectedMenuItem>(DEFAULT_SELECTED_MENU_ITEM);
  const menuItems = useMemo(() => [{
    title: 'ویژه',
    icon: getCdnUrl('/_toad/app-icons/menu/shine-star.svg'),
    id: MobileMenuItems.SpecialOffer,
    isLinkInDrawer: false,
    href: layoutMenu?.promotedUrl || '',
    children: SPECIAL_OFFER_CHILDREN
  }, ...MenuItems].filter(item => !item.hideOnMobile), [layoutMenu?.promotedUrl]);
  return <Transition show={isOpen || PUBLIC_IS_BOT} as={Fragment} afterEnter={disableScroll} beforeLeave={enableScroll} data-sentry-element="Transition" data-sentry-component="CategoryModal" data-sentry-source-file="category-modal.tsx">
            <TransitionChild as={Fragment} enter={PUBLIC_IS_BOT ? undefined : 'transition ease-out duration-300 transform'} enterFrom={PUBLIC_IS_BOT ? undefined : 'translate-y-full'} enterTo={PUBLIC_IS_BOT ? undefined : 'translate-y-0'} leave={PUBLIC_IS_BOT ? undefined : 'transition ease-in duration-200 transform'} leaveFrom={PUBLIC_IS_BOT ? undefined : 'translate-y-0'} leaveTo={PUBLIC_IS_BOT ? undefined : 'translate-y-full'} data-sentry-element="TransitionChild" data-sentry-source-file="category-modal.tsx">
                <div className={cn('fixed inset-0 z-top-of-chatbot-plus3 flex flex-col bg-white lg:hidden', {
        hidden: PUBLIC_IS_BOT && !isOpen
      })}>
                    <div className='flex h-14 w-full shrink-0 items-center gap-2 p-2'>
                        <SearchboxHeader data-sentry-element="SearchboxHeader" data-sentry-source-file="category-modal.tsx" />
                    </div>

                    <div className='mx-2 flex shrink-0 border-b border-border-gray-light'>
                        {MOBILE_MENU_TABS.map(tab => <button key={tab.id} type='button' onClick={() => setActiveTab(tab.id)} className={cn('flex-1 py-3 text-center text-sm font-medium', {
            'border-b-2 border-brand-secondary text-brand-secondary': activeTab === tab.id,
            'text-text-mediumGray': activeTab !== tab.id
          })}>
                                {tab.label}
                            </button>)}
                    </div>

                    <div className='flex min-h-0 flex-1 flex-row overflow-hidden'>
                        <div className={cn('flex min-h-0 flex-1 flex-row overflow-hidden', {
            hidden: activeTab !== 'categories'
          })}>
                            <MenuGrouping selectedItemId={selectedItem?.id} onSelect={setSelectedItem} items={menuItems} data-sentry-element="MenuGrouping" data-sentry-source-file="category-modal.tsx" />
                            <div ref={subMenuScrollRef} className='flex min-h-0 min-w-0 flex-1 flex-col bg-white px-2 pb-14'>
                                {PUBLIC_IS_BOT ? menuItems.map(item => <SubMenuContent key={item.id} id={item.id} title={item.title} parentMenuHref={item.href} subMenuChildren={item.children} className={item.id !== selectedItem.id ? 'hidden' : ''} />) : <>
                                        <SubMenuContent id={selectedItem.id} title={selectedItem.title} parentMenuHref={selectedItem.href} subMenuChildren={selectedItem.subMenuChildren} />
                                    </>}

                                {selectedItem.id === MobileMenuItems.Makeup && <AppNextLink onClick={closeCategoryModal} href='/landing/spring-trends' className='relative mb-1.5 h-12 w-full shrink-0'>
                                        <Image src='https://cdn.khanoumi.com/metro/thumbnails/85856.jpg' alt='ترندهای آرایشی ۱۴۰۵' className='h-full w-full rounded object-cover' unoptimized fill />
                                    </AppNextLink>}
                            </div>
                        </div>

                        <div className={cn('hide-scrollbar min-h-0 min-w-0 flex-1 overflow-y-auto bg-white p-2', {
            hidden: activeTab !== 'brands'
          })}>
                            <Brands type='mobile' data-sentry-element="Brands" data-sentry-source-file="category-modal.tsx" />
                        </div>
                    </div>
                </div>
            </TransitionChild>
        </Transition>;
};
export default CategoryModal;